/**
 * Office locations — list, create, edit and deactivate. WP-W10 shipped list and
 * create; Task T2-C-web (Tier-2 ops, pillars-deepening program) adds the inline edit
 * and the "Tắt"/"Bật lại" toggle T2-C's `PATCH /geo/office-locations/{id}` needed a
 * caller for (`web/shapes/geo.md` §8, updated for both).
 *
 * Rendered only for a session holding `workforce.manage_policies` (`GeoScreen`
 * decides that — never the authorisation, which the server still runs on every
 * call). `radius_m` and `is_active` are **omitted from the create request when
 * left at their default** rather than sent as the default's own value, the same
 * "omit rather than send a placeholder" discipline `hr-leave.md`'s optional `reason`
 * keeps — the server's own default (150m, active) is what a blank field means.
 *
 * ── Edit sends only what actually changed ────────────────────────────────────
 *
 * `PATCH /geo/office-locations/{id}` is a partial update, so `submitEdit` diffs
 * the edit form's four fields against the row it opened from and sends only the
 * ones that differ — never the whole row rewritten to its own current values,
 * which would make a no-op edit look like a real one in the audit log
 * (`office_location.update`) `geo-service.ts` writes for every accepted `PATCH`.
 * The "Tắt"/"Bật lại" toggle is a separate, always-one-field call
 * (`{ is_active: … }`), never routed through the edit form, so it stays true even
 * while a row's edit form is open with unrelated unsaved changes in it.
 *
 * ── Status is icon + label, never colour alone (DESIGN.md §19) ───────────────
 *
 * `Badge` is given `OFFICE_STATUS_PRESENTATION[...].icon` — the presentation
 * entry's own glyph — never `SEVERITY_ICON[severity]` recomputed at the call site.
 * That exact substitution was T2-B's fix-round-1 regression (two status badges
 * silently rendering the wrong icon because it was re-derived from the severity
 * instead of read off the row's own entry) and is not repeated here.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  OfficeLocation,
  OfficeLocationCreateRequest,
  OfficeLocationUpdateRequest,
} from '../../api/geo';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { OFFICE_STATUS_PRESENTATION, officeStatusOf } from '../../geo/geo';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

export interface OfficeLocationsPanelProps {
  readonly status: QueryStatus;
  readonly offices: readonly OfficeLocation[] | null;
  readonly error: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly createPending: boolean;
  readonly createError: WorkforceApiError | null;
  readonly created: OfficeLocation | null;
  readonly onCreate: (body: OfficeLocationCreateRequest) => void;
  readonly updatePending: boolean;
  readonly updateError: WorkforceApiError | null;
  readonly updated: OfficeLocation | null;
  readonly onUpdate: (id: string, body: OfficeLocationUpdateRequest) => void;
}

const RADIUS_MIN_M = 50;
const RADIUS_MAX_M = 3000;

/**
 * `name`'s `maxLength` — NOT the same number on both forms, and that is
 * deliberate rather than an oversight fix round 1 found: `openapi.yaml`'s
 * `OfficeLocationCreateRequest.name` is `maxLength: 200`, but
 * `OfficeLocationUpdateRequest.name` (Task T2-C, migration 048) tightened it to
 * `120` — `officeLocationUpdateSchema` (`geo-service.ts`) enforces the same 120
 * server-side. Each input below is bound to ITS OWN request schema's own limit,
 * never a shared constant, so a 121–200 character edit is refused by the browser
 * before it can reach the server's `400` for it.
 */
const CREATE_NAME_MAX_LENGTH = 200;
const EDIT_NAME_MAX_LENGTH = 120;

export function OfficeLocationsPanel(props: OfficeLocationsPanelProps): ReactNode {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [radiusM, setRadiusM] = useState('');
  const [isActive, setIsActive] = useState(true);

  const ready = name.trim() !== '' && latitude.trim() !== '' && longitude.trim() !== '';

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editLatitude, setEditLatitude] = useState('');
  const [editLongitude, setEditLongitude] = useState('');
  const [editRadiusM, setEditRadiusM] = useState('');

  // A successful edit (or a successful toggle) closes whichever row's edit form
  // was open — the fresh row is on its way back from the re-read `GeoScreen`
  // triggers on every accepted write, so there is nothing left to edit against.
  const { updated } = props;
  useEffect(() => {
    if (updated !== null) setEditingId(null);
  }, [updated]);

  function openEdit(office: OfficeLocation): void {
    setEditingId(office.id);
    setEditName(office.name);
    setEditLatitude(String(office.latitude));
    setEditLongitude(String(office.longitude));
    setEditRadiusM(String(office.radius_m));
  }

  function submitEdit(office: OfficeLocation): void {
    const trimmedName = editName.trim();
    const lat = Number(editLatitude);
    const lng = Number(editLongitude);
    const radius = Number(editRadiusM);

    // `OfficeLocationUpdateRequest`'s members are `readonly` — built by spreading
    // in only the fields that actually differ from the row this form was opened
    // against, never by assigning into a mutable draft, the same "the type itself
    // forbids sending an unchanged field back" discipline the rest of this
    // codebase's PATCH bodies keep.
    const patch: OfficeLocationUpdateRequest = {
      ...(trimmedName !== '' && trimmedName !== office.name ? { name: trimmedName } : {}),
      ...(editLatitude.trim() !== '' && lat !== office.latitude ? { latitude: lat } : {}),
      ...(editLongitude.trim() !== '' && lng !== office.longitude ? { longitude: lng } : {}),
      ...(editRadiusM.trim() !== '' && radius !== office.radius_m ? { radius_m: radius } : {}),
    };
    if (Object.keys(patch).length === 0) {
      setEditingId(null);
      return;
    }
    props.onUpdate(office.id, patch);
  }

  return (
    <section className="wp-section" aria-labelledby="w10-offices-heading" data-testid="w10-offices">
      <h2 className="wp-section__title" id="w10-offices-heading">
        {t('geo.offices.title')}
      </h2>
      <p className="wp-small wp-muted">{t('geo.offices.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || props.createPending) return;
          const lat = Number(latitude);
          const lng = Number(longitude);
          const radius = radiusM.trim() === '' ? undefined : Number(radiusM);
          props.onCreate({
            name: name.trim(),
            latitude: lat,
            longitude: lng,
            ...(radius === undefined ? {} : { radius_m: radius }),
            ...(isActive ? {} : { is_active: false }),
          });
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w10-offices-name">
            {t('geo.offices.create.name')}
          </label>
          <input
            className="wp-field__input"
            id="w10-offices-name"
            data-testid="w10-offices-name"
            type="text"
            value={name}
            maxLength={CREATE_NAME_MAX_LENGTH}
            autoComplete="off"
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w10-offices-lat">
            {t('geo.offices.create.latitude')}
          </label>
          <input
            className="wp-field__input"
            id="w10-offices-lat"
            data-testid="w10-offices-lat"
            type="number"
            step="any"
            min="-90"
            max="90"
            value={latitude}
            onChange={(event) => {
              setLatitude(event.target.value);
            }}
          />
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w10-offices-lng">
            {t('geo.offices.create.longitude')}
          </label>
          <input
            className="wp-field__input"
            id="w10-offices-lng"
            data-testid="w10-offices-lng"
            type="number"
            step="any"
            min="-180"
            max="180"
            value={longitude}
            onChange={(event) => {
              setLongitude(event.target.value);
            }}
          />
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w10-offices-radius">
            {t('geo.offices.create.radius')}
          </label>
          <input
            className="wp-field__input"
            id="w10-offices-radius"
            data-testid="w10-offices-radius"
            type="number"
            min={RADIUS_MIN_M}
            max={RADIUS_MAX_M}
            value={radiusM}
            onChange={(event) => {
              setRadiusM(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('geo.offices.radiusHint')}</p>
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w10-offices-active">
            <input
              id="w10-offices-active"
              data-testid="w10-offices-active"
              type="checkbox"
              checked={isActive}
              onChange={(event) => {
                setIsActive(event.target.checked);
              }}
            />{' '}
            {t('geo.offices.create.active')}
          </label>
        </div>
        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w10-offices-submit"
            disabled={!ready || props.createPending}
          >
            {props.createPending
              ? t('geo.offices.create.submitting')
              : t('geo.offices.create.submit')}
          </button>
        </div>
      </form>

      {props.createError === null ? null : (
        <ApiErrorNote
          error={props.createError}
          subject={t('geo.offices.subject')}
          testId="w10-offices-create-error"
        />
      )}
      {props.created === null ? null : (
        <StatusNote
          severity="low"
          title={t('geo.offices.create.ok', { name: props.created.name })}
          testId="w10-offices-create-ok"
        />
      )}

      {props.status === 'loading' ? (
        <Skeleton
          rows={2}
          variant="block"
          label={t('geo.offices.subject')}
          testId="w10-offices-skeleton"
        />
      ) : null}
      {props.status === 'error' && props.error !== null ? (
        <ApiErrorNote
          error={props.error}
          onRetry={props.onReload}
          subject={t('geo.offices.subject')}
          testId="w10-offices-error"
        />
      ) : null}
      {props.offices === null ? null : props.offices.length === 0 ? (
        <EmptyState
          title={t('geo.offices.empty.title')}
          description={t('geo.offices.empty.body')}
          testId="w10-offices-empty"
        />
      ) : (
        <ul className="wp-rows" data-testid="w10-offices-list">
          {props.offices.map((office) => {
            const displayStatus = officeStatusOf(office);
            const presentation = OFFICE_STATUS_PRESENTATION[displayStatus];
            const isEditing = editingId === office.id;
            return (
              <li className="wp-row" key={office.id} data-testid={`w10-office-${office.id}`}>
                <div className="wp-row__main">
                  <p>
                    <span>{office.name}</span>{' '}
                    <Badge
                      severity={presentation.severity}
                      icon={presentation.icon}
                      label={t(presentation.labelKey)}
                      testId={`w10-office-${office.id}-status`}
                    />
                  </p>
                  <p className="wp-small wp-muted">
                    {t('geo.offices.row.meta', {
                      latitude: office.latitude,
                      longitude: office.longitude,
                      radius: office.radius_m,
                    })}
                  </p>

                  {isEditing ? (
                    <form
                      className="wp-filters"
                      data-testid={`w10-office-${office.id}-edit-form`}
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (props.updatePending) return;
                        submitEdit(office);
                      }}
                    >
                      <div className="wp-filters__group">
                        <label
                          className="wp-field__label"
                          htmlFor={`w10-office-${office.id}-edit-name`}
                        >
                          {t('geo.offices.edit.name')}
                        </label>
                        <input
                          className="wp-field__input"
                          id={`w10-office-${office.id}-edit-name`}
                          data-testid={`w10-office-${office.id}-edit-name`}
                          type="text"
                          value={editName}
                          maxLength={EDIT_NAME_MAX_LENGTH}
                          onChange={(event) => {
                            setEditName(event.target.value);
                          }}
                        />
                      </div>
                      <div className="wp-filters__group">
                        <label
                          className="wp-field__label"
                          htmlFor={`w10-office-${office.id}-edit-lat`}
                        >
                          {t('geo.offices.edit.latitude')}
                        </label>
                        <input
                          className="wp-field__input"
                          id={`w10-office-${office.id}-edit-lat`}
                          data-testid={`w10-office-${office.id}-edit-lat`}
                          type="number"
                          step="any"
                          min="-90"
                          max="90"
                          value={editLatitude}
                          onChange={(event) => {
                            setEditLatitude(event.target.value);
                          }}
                        />
                      </div>
                      <div className="wp-filters__group">
                        <label
                          className="wp-field__label"
                          htmlFor={`w10-office-${office.id}-edit-lng`}
                        >
                          {t('geo.offices.edit.longitude')}
                        </label>
                        <input
                          className="wp-field__input"
                          id={`w10-office-${office.id}-edit-lng`}
                          data-testid={`w10-office-${office.id}-edit-lng`}
                          type="number"
                          step="any"
                          min="-180"
                          max="180"
                          value={editLongitude}
                          onChange={(event) => {
                            setEditLongitude(event.target.value);
                          }}
                        />
                      </div>
                      <div className="wp-filters__group">
                        <label
                          className="wp-field__label"
                          htmlFor={`w10-office-${office.id}-edit-radius`}
                        >
                          {t('geo.offices.edit.radius')}
                        </label>
                        <input
                          className="wp-field__input"
                          id={`w10-office-${office.id}-edit-radius`}
                          data-testid={`w10-office-${office.id}-edit-radius`}
                          type="number"
                          min={RADIUS_MIN_M}
                          max={RADIUS_MAX_M}
                          value={editRadiusM}
                          onChange={(event) => {
                            setEditRadiusM(event.target.value);
                          }}
                        />
                        <p className="wp-small wp-muted">{t('geo.offices.radiusHint')}</p>
                      </div>
                      <div className="wp-filters__actions">
                        <button
                          className="wp-button wp-button--primary"
                          type="submit"
                          data-testid={`w10-office-${office.id}-edit-save`}
                          disabled={props.updatePending}
                        >
                          {props.updatePending
                            ? t('geo.offices.edit.saving')
                            : t('geo.offices.edit.save')}
                        </button>
                        <button
                          className="wp-button wp-button--quiet"
                          type="button"
                          data-testid={`w10-office-${office.id}-edit-cancel`}
                          onClick={() => {
                            setEditingId(null);
                          }}
                        >
                          {t('geo.offices.edit.cancel')}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="wp-filters__actions">
                      <button
                        className="wp-button wp-button--quiet"
                        type="button"
                        data-testid={`w10-office-${office.id}-edit-open`}
                        onClick={() => {
                          openEdit(office);
                        }}
                      >
                        {t('geo.offices.edit.open')}
                      </button>
                      <button
                        className="wp-button wp-button--quiet"
                        type="button"
                        data-testid={`w10-office-${office.id}-toggle`}
                        disabled={props.updatePending}
                        onClick={() => {
                          props.onUpdate(office.id, { is_active: !office.is_active });
                        }}
                      >
                        {office.is_active ? t('geo.offices.deactivate') : t('geo.offices.activate')}
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {props.updateError === null ? null : (
        <ApiErrorNote
          error={props.updateError}
          subject={t('geo.offices.subject')}
          testId="w10-offices-update-error"
        />
      )}
    </section>
  );
}
